<!--
  SkyPlus Search Component
  Location: app/src/app/components/skyplus/search/skyplus-search.component.html
-->

<div class="sp-search-container">
  
  <!-- Search Header -->
  <div class="sp-search-header">
    <div class="sp-search-header-left">
      <div class="sp-search-icon">
        <span class="material-icons">search</span>
      </div>
      <div class="sp-search-title">
        <h1>Search Documents</h1>
        <p>Find documents by name or advanced criteria</p>
      </div>
    </div>
    
    <!-- Search Mode Toggle -->
    <div class="sp-search-mode-toggle">
      <button class="sp-mode-btn" 
              [class.active]="searchMode === 'simple'" 
              (click)="setSearchMode('simple')">
        <span class="material-icons">search</span>
        Simple Search
      </button>
      <button class="sp-mode-btn" 
              [class.active]="searchMode === 'advanced'" 
              (click)="setSearchMode('advanced')">
        <span class="material-icons">tune</span>
        Advanced Search
      </button>
    </div>
  </div>
  
  <!-- Search Content -->
  <div class="sp-search-content" [class.has-results]="hasSearched">
    
    <!-- ==================== SIMPLE SEARCH ==================== -->
    <div class="sp-simple-search" *ngIf="searchMode === 'simple'">
      <div class="sp-search-input-wrapper">
        <span class="material-icons search-icon">search</span>
        <input type="text" 
               class="sp-search-input"
               [(ngModel)]="simpleSearchTerm"
               (input)="onSimpleSearchInput($event)"
               (keyup.enter)="executeSimpleSearch()"
               (blur)="hideSuggestions()"
               placeholder="Search by file name, title, or content..."
               autocomplete="off">
        <button class="sp-search-btn" 
                (click)="executeSimpleSearch()"
                [disabled]="!simpleSearchTerm.trim() || isSearching">
          <span class="material-icons" *ngIf="!isSearching">arrow_forward</span>
          <span class="material-icons spinning" *ngIf="isSearching">autorenew</span>
        </button>
        
        <!-- Suggestions Dropdown -->
        <div class="sp-suggestions" *ngIf="showSuggestions && suggestions.length > 0">
          <div class="sp-suggestion-item" 
               *ngFor="let suggestion of suggestions"
               (click)="selectSuggestion(suggestion)">
            <span class="material-icons">history</span>
            <span>{{ suggestion }}</span>
          </div>
        </div>
      </div>
      
      <p class="sp-search-hint">
        <span class="material-icons">info</span>
        Press Enter or click the arrow to search. Use * for wildcards.
      </p>
    </div>
    
    <!-- ==================== ADVANCED SEARCH ==================== -->
    <div class="sp-advanced-search" *ngIf="searchMode === 'advanced'">
      
      <!-- Document Type Selector -->
      <div class="sp-search-section">
        <h3 class="sp-section-title">
          <span class="material-icons">category</span>
          Document Type
        </h3>
        
        <div class="sp-doc-type-grid">
          <!-- All Documents Option -->
          <div class="sp-doc-type-card" 
               [class.selected]="selectedDocumentType === null"
               (click)="selectDocumentType(null)">
            <div class="sp-doc-type-icon" style="background: #757575;">
              <span class="material-icons">folder_open</span>
            </div>
            <div class="sp-doc-type-info">
              <span class="sp-doc-type-name">All Documents</span>
              <span class="sp-doc-type-desc">Search across all document types</span>
            </div>
          </div>
          
          <!-- Document Type Cards -->
          <div class="sp-doc-type-card" 
               *ngFor="let docType of documentTypes"
               [class.selected]="selectedDocumentType?.id === docType.id"
               (click)="selectDocumentType(docType)">
            <div class="sp-doc-type-icon" [style.background]="docType.color">
              <span class="material-icons">{{ docType.icon }}</span>
            </div>
            <div class="sp-doc-type-info">
              <span class="sp-doc-type-name">{{ docType.name }}</span>
              <span class="sp-doc-type-desc">{{ docType.description }}</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Dynamic Fields (Based on Selected Document Type) -->
      <div class="sp-search-section" *ngIf="selectedDocumentType">
        <h3 class="sp-section-title">
          <span class="material-icons">edit_note</span>
          {{ selectedDocumentType.name }} Fields
        </h3>
        
        <div class="sp-fields-grid">
          <div class="sp-field" *ngFor="let field of searchableFields(); trackBy: trackByFieldName">
            <label class="sp-field-label">{{ field.label }}</label>
            
            <!-- Text Input -->
            <input *ngIf="field.type === 'text'"
                   type="text"
                   class="sp-field-input"
                   [(ngModel)]="fieldValues[field.propertyName]"
                   [placeholder]="field.placeholder || 'Enter ' + field.label">
            
            <!-- Number Input -->
            <input *ngIf="field.type === 'number'"
                   type="number"
                   class="sp-field-input"
                   [(ngModel)]="fieldValues[field.propertyName]"
                   [placeholder]="field.placeholder || 'Enter ' + field.label">
            
            <!-- Date Input -->
            <input *ngIf="field.type === 'date'"
                   type="date"
                   class="sp-field-input"
                   [(ngModel)]="fieldValues[field.propertyName]">
            
            <!-- Select Input -->
            <select *ngIf="field.type === 'select'"
                    class="sp-field-input"
                    [(ngModel)]="fieldValues[field.propertyName]">
              <option value="">-- Select {{ field.label }} --</option>
              <option *ngFor="let opt of field.options" [value]="opt.value">
                {{ opt.label }}
              </option>
            </select>
            
            <!-- Textarea -->
            <textarea *ngIf="field.type === 'textarea'"
                      class="sp-field-input sp-textarea"
                      [(ngModel)]="fieldValues[field.propertyName]"
                      [placeholder]="field.placeholder || 'Enter ' + field.label"
                      rows="2"></textarea>
          </div>
        </div>
      </div>
      
      <!-- Date Range Filter -->
      <div class="sp-search-section">
        <h3 class="sp-section-title">
          <span class="material-icons">date_range</span>
          Date Range (Optional)
        </h3>
        
        <div class="sp-date-range">
          <div class="sp-field">
            <label class="sp-field-label">From</label>
            <input type="date" 
                   class="sp-field-input"
                   [(ngModel)]="dateFrom">
          </div>
          <div class="sp-field">
            <label class="sp-field-label">To</label>
            <input type="date" 
                   class="sp-field-input"
                   [(ngModel)]="dateTo">
          </div>
        </div>
      </div>
      
      <!-- Search Actions -->
      <div class="sp-search-actions">
        <button class="sp-btn sp-btn-secondary" (click)="clearSearch()">
          <span class="material-icons">clear</span>
          Clear
        </button>
        <button class="sp-btn sp-btn-primary" 
                (click)="executeAdvancedSearch()"
                [disabled]="isSearching">
          <span class="material-icons" *ngIf="!isSearching">search</span>
          <span class="material-icons spinning" *ngIf="isSearching">autorenew</span>
          {{ isSearching ? 'Searching...' : 'Search' }}
        </button>
      </div>
    </div>
    
    <!-- ==================== SEARCH RESULTS ==================== -->
    <div class="sp-search-results" *ngIf="hasSearched">
      
      <!-- Results Header -->
      <div class="sp-results-header">
        <div class="sp-results-info">
          <span class="material-icons">fact_check</span>
          <span *ngIf="totalResults > 0">Found {{ totalResults }} result(s)</span>
          <span *ngIf="totalResults === 0">No results found</span>
        </div>
        
        <div class="sp-results-actions" *ngIf="totalResults > 0">
          <div class="sp-view-toggle">
            <button class="sp-view-btn" 
                    [class.active]="viewMode === 'list'" 
                    (click)="setViewMode('list')" 
                    title="List View">
              <span class="material-icons">view_list</span>
            </button>
            <button class="sp-view-btn" 
                    [class.active]="viewMode === 'grid'" 
                    (click)="setViewMode('grid')" 
                    title="Grid View">
              <span class="material-icons">grid_view</span>
            </button>
          </div>
        </div>
      </div>
      
      <!-- No Results -->
      <div class="sp-no-results" *ngIf="totalResults === 0 && !isSearching">
        <span class="material-icons">search_off</span>
        <h3>No documents found</h3>
        <p>Try adjusting your search criteria or use different keywords.</p>
      </div>
      
      <!-- Results List View -->
      <div class="sp-results-list" *ngIf="viewMode === 'list' && totalResults > 0">
        <table class="sp-results-table">
          <thead>
            <tr>
              <th class="col-icon"></th>
              <th class="col-name">Name</th>
              <th class="col-location">Location</th>
              <th class="col-size">Size</th>
              <th class="col-modified">Modified</th>
              <th class="col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let result of searchResults; trackBy: trackByResultId"
                [class.selected]="selectedResult?.id === result.id"
                (click)="selectResult(result, $event)"
                (dblclick)="openResult(result)">
              <td class="col-icon">
                <span class="material-icons" [style.color]="result.iconColor">{{ result.icon }}</span>
              </td>
              <td class="col-name">
                <div class="sp-result-name">
                  <span class="name">{{ result.name }}</span>
                  <span class="title" *ngIf="result.title">{{ result.title }}</span>
                </div>
              </td>
              <td class="col-location">
                <span class="sp-result-path" (click)="navigateToLocation(result, $event)">
                  {{ formatPath(result.path) }}
                </span>
              </td>
              <td class="col-size">{{ result.sizeFormatted || '-' }}</td>
              <td class="col-modified">
                <div class="sp-result-modified">
                  <span class="date">{{ formatDate(result.modifiedAt) }}</span>
                  <span class="user">{{ result.modifiedBy }}</span>
                </div>
              </td>
              <td class="col-actions">
                <button class="sp-action-btn" (click)="openResult(result)" title="Open">
                  <span class="material-icons">open_in_new</span>
                </button>
                <button class="sp-action-btn" (click)="downloadResult(result, $event)" title="Download" *ngIf="!result.isFolder">
                  <span class="material-icons">download</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- Results Grid View -->
      <div class="sp-results-grid" *ngIf="viewMode === 'grid' && totalResults > 0">
        <div class="sp-result-card" 
             *ngFor="let result of searchResults; trackBy: trackByResultId"
             [class.selected]="selectedResult?.id === result.id"
             (click)="selectResult(result, $event)"
             (dblclick)="openResult(result)">
          <div class="sp-card-icon" [style.background]="result.iconColor + '20'">
            <span class="material-icons" [style.color]="result.iconColor">{{ result.icon }}</span>
          </div>
          <div class="sp-card-info">
            <span class="sp-card-name">{{ result.name }}</span>
            <span class="sp-card-meta">{{ result.sizeFormatted || 'Folder' }}</span>
          </div>
          <div class="sp-card-actions">
            <button class="sp-action-btn" (click)="openResult(result)" title="Open">
              <span class="material-icons">open_in_new</span>
            </button>
            <button class="sp-action-btn" (click)="downloadResult(result, $event)" title="Download" *ngIf="!result.isFolder">
              <span class="material-icons">download</span>
            </button>
          </div>
        </div>
      </div>
      
      <!-- Pagination -->
      <div class="sp-pagination" *ngIf="totalResults > pageSize">
        <span class="sp-page-info">Showing {{ startItem }}-{{ endItem }} of {{ totalResults }}</span>
        <div class="sp-page-controls">
          <button class="sp-page-btn" 
                  [disabled]="currentPage === 1" 
                  (click)="onPageChange(currentPage - 1)">
            <span class="material-icons">chevron_left</span>
            Prev
          </button>
          <span class="sp-page-number">{{ currentPage }}</span>
          <button class="sp-page-btn" 
                  [disabled]="currentPage >= totalPages" 
                  (click)="onPageChange(currentPage + 1)">
            Next
            <span class="material-icons">chevron_right</span>
          </button>
        </div>
        <div class="sp-page-size">
          <span>Items per page:</span>
          <select [(ngModel)]="pageSize" (change)="onPageSizeChange(pageSize)">
            <option *ngFor="let size of pageSizeOptions" [value]="size">{{ size }}</option>
          </select>
        </div>
      </div>
      
    </div>
    
  </div>
  
  <!-- Properties Panel -->
  <aside class="sp-properties-panel" *ngIf="showPropertiesPanel && selectedResult">
    <div class="sp-properties-header">
      <div class="sp-properties-title">
        <span class="material-icons" [style.color]="selectedResult.iconColor">{{ selectedResult.icon }}</span>
        <span class="sp-doc-name">{{ selectedResult.name }}</span>
      </div>
      <button class="sp-close-btn" (click)="closePropertiesPanel()">
        <span class="material-icons">close</span>
      </button>
    </div>
    
    <div class="sp-properties-actions">
      <button class="sp-action-btn" (click)="openResult(selectedResult)" title="Open">
        <span class="material-icons">open_in_new</span>
      </button>
      <button class="sp-action-btn" (click)="downloadResult(selectedResult)" title="Download" *ngIf="!selectedResult.isFolder">
        <span class="material-icons">download</span>
      </button>
      <button class="sp-action-btn" (click)="navigateToLocation(selectedResult)" title="Go to location">
        <span class="material-icons">folder_open</span>
      </button>
    </div>
    
    <div class="sp-properties-content">
      <div class="sp-property-item">
        <span class="sp-prop-icon material-icons">description</span>
        <div class="sp-prop-content">
          <span class="sp-prop-label">Name</span>
          <span class="sp-prop-value">{{ selectedResult.name }}</span>
        </div>
      </div>
      
      <div class="sp-property-item" *ngIf="selectedResult.title">
        <span class="sp-prop-icon material-icons">title</span>
        <div class="sp-prop-content">
          <span class="sp-prop-label">Title</span>
          <span class="sp-prop-value">{{ selectedResult.title }}</span>
        </div>
      </div>
      
      <div class="sp-property-item" *ngIf="selectedResult.description">
        <span class="sp-prop-icon material-icons">notes</span>
        <div class="sp-prop-content">
          <span class="sp-prop-label">Description</span>
          <span class="sp-prop-value">{{ selectedResult.description }}</span>
        </div>
      </div>
      
      <div class="sp-property-item">
        <span class="sp-prop-icon material-icons">folder_open</span>
        <div class="sp-prop-content">
          <span class="sp-prop-label">Location</span>
          <span class="sp-prop-value">{{ formatPath(selectedResult.path) }}</span>
        </div>
      </div>
      
      <div class="sp-property-item" *ngIf="selectedResult.mimeType">
        <span class="sp-prop-icon material-icons">category</span>
        <div class="sp-prop-content">
          <span class="sp-prop-label">Type</span>
          <span class="sp-prop-value">{{ selectedResult.mimeType }}</span>
        </div>
      </div>
      
      <div class="sp-property-item" *ngIf="selectedResult.size">
        <span class="sp-prop-icon material-icons">straighten</span>
        <div class="sp-prop-content">
          <span class="sp-prop-label">Size</span>
          <span class="sp-prop-value">{{ selectedResult.sizeFormatted }}</span>
        </div>
      </div>
      
      <div class="sp-property-item">
        <span class="sp-prop-icon material-icons">event</span>
        <div class="sp-prop-content">
          <span class="sp-prop-label">Created</span>
          <span class="sp-prop-value">{{ formatDate(selectedResult.createdAt) }}</span>
        </div>
      </div>
      
      <div class="sp-property-item">
        <span class="sp-prop-icon material-icons">person</span>
        <div class="sp-prop-content">
          <span class="sp-prop-label">Created By</span>
          <span class="sp-prop-value">{{ selectedResult.createdBy }}</span>
        </div>
      </div>
      
      <div class="sp-property-item">
        <span class="sp-prop-icon material-icons">update</span>
        <div class="sp-prop-content">
          <span class="sp-prop-label">Modified</span>
          <span class="sp-prop-value">{{ formatDate(selectedResult.modifiedAt) }}</span>
        </div>
      </div>
      
      <div class="sp-property-item">
        <span class="sp-prop-icon material-icons">person_outline</span>
        <div class="sp-prop-content">
          <span class="sp-prop-label">Modified By</span>
          <span class="sp-prop-value">{{ selectedResult.modifiedBy }}</span>
        </div>
      </div>
      
      <!-- Custom Properties (if available) -->
      <ng-container *ngIf="selectedResult.properties && getCustomProperties(selectedResult.properties).length > 0">
        <div class="sp-section-divider"></div>
        <h4 class="sp-section-title">Custom Properties</h4>
        
        <div class="sp-property-item" *ngFor="let prop of getCustomProperties(selectedResult.properties)">
          <span class="sp-prop-icon material-icons">label</span>
          <div class="sp-prop-content">
            <span class="sp-prop-label">{{ prop.label }}</span>
            <span class="sp-prop-value">{{ prop.value }}</span>
          </div>
        </div>
      </ng-container>
    </div>
  </aside>
  
</div>